import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../firebase';
import { fetchInvite, acceptInvite } from '../services/consoleApi';
import '../site-v3/legacy-v3.css'; // Barlow, the trasseapp.com face (2026-10-28)

/* Public page reached from a console invite email: /console/accept?token=…
   The email is prefilled + already verified (they received the mail); they just
   set a password. On success we sign them in and drop them into the console. */
const AcceptConsoleInvite: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      if (!token) { setLoading(false); return; }
      const r = await fetchInvite(token);
      if (!live) return;
      if (r.ok && r.email) setEmail(r.email);
      setLoading(false);
    })();
    return () => { live = false; };
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) { setError('Choose a password of at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSubmitting(true);
    const r = await acceptInvite(token, password);
    if (!r.ok || !r.email) { setError(r.error || 'Could not complete signup.'); setSubmitting(false); return; }
    try {
      await signInWithEmailAndPassword(auth, r.email, password);
      navigate('/admin', { replace: true });
    } catch {
      // Account is set up; if auto sign-in hiccups, send them to login.
      navigate('/login', { replace: true });
    }
  };

  const card: React.CSSProperties = {
    maxWidth: 420, margin: '8vh auto', padding: 28, borderRadius: 14,
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)', background: '#fff', fontFamily: 'Barlow, -apple-system, Segoe UI, Roboto, sans-serif', color: '#0F1419',
  };
  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '11px 12px', margin: '6px 0 14px',
    border: '1px solid #D6D3CC', borderRadius: 9, fontSize: 14,
  };
  const button: React.CSSProperties = {
    width: '100%', padding: '12px', background: '#00707F', color: '#fff', border: 'none',
    borderRadius: 9, fontWeight: 700, fontSize: 14, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1,
  };

  if (loading) return <div style={card}>Checking your invite…</div>;

  if (!token || !email) {
    return (
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: 18, color: '#00707F', marginBottom: 10 }}>trasse Console</div>
        <h2 style={{ fontSize: 18, margin: '0 0 8px' }}>Invite not valid</h2>
        <p style={{ fontSize: 14, color: '#2A3138', lineHeight: 1.55 }}>
          This invite link is invalid, already used, or expired. Ask a PEAR admin to send you a new one, or
          contact <a href="mailto:pearnet104@gmail.com">pearnet104@gmail.com</a>.
        </p>
      </div>
    );
  }

  return (
    <div style={card}>
      <div style={{ fontWeight: 800, fontSize: 18, color: '#00707F', marginBottom: 10 }}>trasse Console</div>
      <h2 style={{ fontSize: 18, margin: '0 0 6px' }}>Set your password</h2>
      <p style={{ fontSize: 13.5, color: '#5D646B', lineHeight: 1.5, margin: '0 0 16px' }}>
        You're joining the PEAR admin console. Your email is verified — just choose a password.
      </p>
      <form onSubmit={onSubmit}>
        <label style={{ fontSize: 12.5, color: '#5D646B', fontWeight: 600 }}>Email</label>
        <input style={{ ...input, background: '#F6F5F1', color: '#5D646B' }} value={email} readOnly />
        <label style={{ fontSize: 12.5, color: '#5D646B', fontWeight: 600 }}>Password</label>
        <input style={input} type="password" autoComplete="new-password" value={password}
          onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
        <label style={{ fontSize: 12.5, color: '#5D646B', fontWeight: 600 }}>Confirm password</label>
        <input style={input} type="password" autoComplete="new-password" value={confirm}
          onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
        {error && <div style={{ color: '#B42318', fontSize: 13, margin: '0 0 12px' }}>{error}</div>}
        <button style={button} type="submit" disabled={submitting}>
          {submitting ? 'Setting up…' : 'Create account & sign in'}
        </button>
      </form>
    </div>
  );
};

export default AcceptConsoleInvite;
